import {
  AddExternalCardInput,
  AddExternalCardResultTypenames,
  AnalyticsEvent,
  ButtonComponentV2,
  Mutation,
  VerifyCardActionScreen,
} from '@varo/graphql-schema-types';
import { logGenericErrorEvents } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { getTabapayErrorScreen } from 'features/myVaro/screens/ExternalAccounts/helper/TabapayHelper';
import { useEffect, useState } from 'react';
import AddExternalCard from 'services/graphql/mutations/AddExternalCard';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { useMutation } from 'urql';

const useAddExternalCard = () => {
  const [result, executeMutation] = useMutation<Mutation>(AddExternalCard);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [externalCardId, setExternalCardId] = useState<string>('');
  const [verifyCardActionScreen, setVerifyCardActionScreen] =
    useState<VerifyCardActionScreen>();
  const { segmentClient } = useAnalyticsConfiguration();
  const [errorScreen, setErrorScreen] = useState<{
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    analytics?: AnalyticsEvent;
  } | null>(null);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const responseError = useFormattedCombinedError(result.error);

  useEffect(() => {
    if (responseError) {
      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError.title,
        messageBody: responseError.message,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.error]);

  const addExternalCard = async (input: AddExternalCardInput) => {
    try {
      setIsLoading(true);
      setErrorScreen(null);
      const result = await executeMutation({
        idempotencyId: getUuid(),
        input: input,
      });
      const errorScreen = getTabapayErrorScreen(result?.data?.addExternalCard);
      if (errorScreen) {
        setErrorScreen(errorScreen);
      }

      if (
        result?.data?.addExternalCard?.__typename ===
        AddExternalCardResultTypenames.AddExternalCardResultError
      ) {
        logGenericErrorEvents(
          'External Card Details',
          'Generic_Error',
          segmentClient,
          errorScreen?.code,
          errorScreen?.messageBody,
        );
        setErrorScreen({
          screenType: ErrorScreenType.Error,
          messageHeadline: result.data.addExternalCard.title,
          messageBody: result.data.addExternalCard.message,
        });
      }

      if (
        result?.data?.addExternalCard?.__typename ===
        AddExternalCardResultTypenames.AddExternalCardResultSuccess
      ) {
        setExternalCardId(result.data.addExternalCard.externalCardId);
        setVerifyCardActionScreen(
          result.data.addExternalCard.verifyCardActionScreen,
        );
      }
    } catch (error) {
      let errorTitle: string = defaultTitle;
      let errorMessage: string = defaultMessage;

      if (isCoreError(error)) {
        errorTitle = error.title;
        errorMessage = error.message;
      } else if (isCombinedError(error)) {
        errorMessage = error.message;
      }

      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: errorTitle,
        messageBody: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetCardData = () => {
    setExternalCardId('');
    setVerifyCardActionScreen(undefined);
  };

  const resetError = () => {
    setErrorScreen(null);
  };

  return {
    addExternalCard,
    externalCardId,
    isLoading,
    verifyCardActionScreen,
    errorScreen,
    resetCardData,
    resetError,
  };
};

export default useAddExternalCard;
